import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    Users,
    Bike,
    Wrench,
    LogOut,
    CalendarDays,
    ShieldCheck,
    Building2,
    Star,
    UserPlus,
    CalendarClock,
    FileBarChart2,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { api } from "../lib/api";

const nav = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard" },
    { to: "/clientes", icon: Users, label: "Clientes", testid: "nav-clientes" },
    { to: "/motos", icon: Bike, label: "Motos", testid: "nav-motos" },
    { to: "/servicos", icon: Wrench, label: "Serviços", testid: "nav-servicos" },
];

const adminNav = [
    { to: "/backoffice/aprovacoes", icon: UserPlus, label: "Aprovações", testid: "nav-backoffice-aprovacoes", showPendingKey: "clientes" },
    { to: "/backoffice/disponibilidade", icon: CalendarClock, label: "Disponibilidade", testid: "nav-backoffice-slots" },
    { to: "/backoffice/avaliacoes", icon: Star, label: "Avaliações", testid: "nav-backoffice-avaliacoes", showPendingKey: "reviews" },
    { to: "/backoffice/relatorios", icon: FileBarChart2, label: "Relatórios", testid: "nav-backoffice-relatorios" },
    { to: "/backoffice", icon: ShieldCheck, label: "Colaboradores", testid: "nav-backoffice" },
    { to: "/backoffice/oficina", icon: Building2, label: "Identidade Oficina", testid: "nav-backoffice-oficina" },
];

export default function AppLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [pendingReviews, setPendingReviews] = useState(0);
    const [pendingClientes, setPendingClientes] = useState(0);

    useEffect(() => {
        if (user?.role !== "admin") return;
        let mounted = true;
        const fetchPending = () =>
            api
                .get("/admin/pending-total")
                .then((r) => {
                    if (!mounted) return;
                    setPendingReviews(r.data.reviews || 0);
                    setPendingClientes(r.data.clientes || 0);
                })
                .catch(() => {});
        fetchPending();
        const interval = setInterval(fetchPending, 30000);
        return () => {
            mounted = false;
            clearInterval(interval);
        };
    }, [user]);

    async function handleLogout() {
        await logout();
        navigate("/login", { replace: true });
    }

    return (
        <div className="flex min-h-screen bg-[#FAFAFA]">
            <aside
                data-testid="app-sidebar"
                className="hidden md:flex w-64 flex-col border-r border-zinc-200 bg-white"
            >
                <div className="flex items-center gap-3 border-b border-zinc-200 px-6 py-5">
                    <div className="flex h-9 w-9 items-center justify-center rounded-sm bg-[#FF3B30] text-white">
                        <Wrench className="h-4 w-4" strokeWidth={2} />
                    </div>
                    <div>
                        <div className="font-['Outfit'] text-base font-semibold tracking-tight text-zinc-900">
                            Oficina Moto
                        </div>
                        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">
                            Gestão · PT
                        </div>
                    </div>
                </div>

                <nav className="flex-1 px-3 py-6">
                    <div className="px-3 pb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">
                        Navegação
                    </div>
                    <ul className="space-y-1">
                        {nav.map(({ to, icon: Icon, label, testid }) => (
                            <li key={to}>
                                <NavLink
                                    to={to}
                                    data-testid={testid}
                                    className={({ isActive }) =>
                                        `flex items-center gap-3 rounded-sm px-3 py-2.5 text-sm font-medium transition-colors ${
                                            isActive
                                                ? "bg-[#FF3B30] text-white"
                                                : "text-zinc-700 hover:bg-zinc-100"
                                        }`
                                    }
                                >
                                    <Icon className="h-4 w-4" strokeWidth={1.75} />
                                    {label}
                                </NavLink>
                            </li>
                        ))}
                    </ul>

                    {user?.role === "admin" ? (
                        <>
                            <div className="mt-6 px-3 pb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">
                                Administração
                            </div>
                            <ul className="space-y-1">
                                {adminNav.map(({ to, icon: Icon, label, testid, showPendingKey }) => {
                                    const count =
                                        showPendingKey === "clientes" ? pendingClientes :
                                        showPendingKey === "reviews" ? pendingReviews : 0;
                                    return (
                                    <li key={to}>
                                        <NavLink
                                            to={to}
                                            data-testid={testid}
                                            className={({ isActive }) =>
                                                `flex items-center justify-between gap-3 rounded-sm px-3 py-2.5 text-sm font-medium transition-colors ${
                                                    isActive
                                                        ? "bg-zinc-900 text-white"
                                                        : "text-zinc-700 hover:bg-zinc-100"
                                                }`
                                            }
                                        >
                                            <span className="flex items-center gap-3">
                                                <Icon className="h-4 w-4" strokeWidth={1.75} />
                                                {label}
                                            </span>
                                            {showPendingKey && count > 0 ? (
                                                <span
                                                    data-testid={`pending-badge-${showPendingKey}`}
                                                    className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-sm bg-[#FF3B30] px-1.5 text-[10px] font-bold text-white"
                                                >
                                                    {count}
                                                </span>
                                            ) : null}
                                        </NavLink>
                                    </li>
                                    );
                                })}
                            </ul>
                        </>
                    ) : null}
                </nav>

                <div className="border-t border-zinc-200 p-4">
                    <div className="mb-3 px-2">
                        <div className="text-xs font-semibold text-zinc-900" data-testid="sidebar-user-name">
                            {user?.name || "Operador"}
                        </div>
                        <div className="text-[11px] text-zinc-500">{user?.email}</div>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-start rounded-sm"
                        onClick={handleLogout}
                        data-testid="logout-btn"
                    >
                        <LogOut className="mr-2 h-4 w-4" strokeWidth={1.75} />
                        Terminar sessão
                    </Button>
                </div>
            </aside>

            {/* Mobile top bar */}
            <div className="md:hidden flex items-center justify-between border-b border-zinc-200 bg-white px-4 py-3 fixed top-0 inset-x-0 z-20">
                <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-[#FF3B30] text-white">
                        <Wrench className="h-4 w-4" />
                    </div>
                    <span className="font-['Outfit'] font-semibold">Oficina Moto</span>
                </div>
                <Button size="sm" variant="ghost" onClick={handleLogout} data-testid="logout-btn-mobile">
                    <LogOut className="h-4 w-4" />
                </Button>
            </div>

            <main className="flex-1 md:pt-0 pt-14">
                <div className="mx-auto w-full max-w-7xl px-6 py-8 md:px-10 md:py-10">
                    <Outlet />
                </div>
            </main>

            {/* Mobile bottom nav */}
            <nav className="md:hidden fixed bottom-0 inset-x-0 z-20 border-t border-zinc-200 bg-white">
                <ul className="grid grid-cols-4">
                    {nav.map(({ to, icon: Icon, label, testid }) => (
                        <li key={to}>
                            <NavLink
                                to={to}
                                data-testid={`${testid}-mobile`}
                                className={({ isActive }) =>
                                    `flex flex-col items-center justify-center gap-1 py-2 text-[10px] ${
                                        isActive ? "text-[#FF3B30]" : "text-zinc-600"
                                    }`
                                }
                            >
                                <Icon className="h-5 w-5" strokeWidth={1.75} />
                                {label}
                            </NavLink>
                        </li>
                    ))}
                </ul>
            </nav>
        </div>
    );
}
